import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  tier: 'free' | 'premium';
  tokensRemaining: number;
  projectsCount: number;
}

export interface AuthState {
  // Authentication
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  
  // Actions
  setToken: (token: string) => void;
  setUser: (user: User) => void;
  logout: () => void;
  
  // Token management
  refreshToken: () => Promise<void>;
  validateToken: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // State
      token: null,
      user: null,
      isAuthenticated: false,
      
      // Actions
      setToken: (token: string) => {
        set({ token, isAuthenticated: true });
        
        // Decode token to extract user info (simplified)
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const user: User = {
            uid: payload.user_id || payload.sub,
            email: payload.email,
            displayName: payload.name,
            photoURL: payload.picture,
            tier: 'free', // Default, will be updated from API
            tokensRemaining: 5000, // Default
            projectsCount: 0,
          };
          
          set({ user });
          
          // Fetch full user context from API
          get().fetchUserContext();
        } catch (error) {
          console.error('Failed to decode token:', error);
        }
      },
      
      setUser: (user: User) => {
        set({ user });
      },
      
      logout: () => {
        set({ 
          token: null, 
          user: null, 
          isAuthenticated: false 
        });
        localStorage.removeItem('stan_token');
      },
      
      // Token management
      refreshToken: async () => {
        // In a real implementation, this would refresh the Firebase token
        // For now, we'll just validate the existing token
        const isValid = await get().validateToken();
        if (!isValid) {
          get().logout();
        }
      },
      
      validateToken: async (): Promise<boolean> => {
        const { token } = get();
        if (!token) return false;
        
        try {
          // Check if token is expired
          const payload = JSON.parse(atob(token.split('.')[1]));
          const now = Date.now() / 1000;
          
          if (payload.exp && payload.exp < now) {
            console.log('Token expired');
            return false;
          }
          
          return true;
        } catch (error) {
          console.error('Token validation failed:', error);
          return false;
        }
      },
      
      // Fetch user context from API
      fetchUserContext: async () => {
        const { token } = get();
        if (!token) return;
        
        try {
          const response = await fetch('https://stan-backend.stanleyisaac134.workers.dev/api/user/context', {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            set((state) => ({
              user: state.user ? {
                ...state.user,
                tier: data.tier || 'free',
                tokensRemaining: data.tokens_remaining || 0,
                projectsCount: data.projects_count || 0,
              } : null,
            }));
          }
        } catch (error) {
          console.error('Failed to fetch user context:', error);
        }
      },
    }),
    {
      name: 'stan-auth-store',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);